import useCloseWhenMissing, { HOME } from '../hooks/useCloseWhenMissing'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { LuCopy, LuTrash, LuCheck, LuEye, LuEyeOff } from 'react-icons/lu'
import useClipboard from '../hooks/useClipboard'
import useNetwork from '../models/useNetwork'
import useToken from '../models/useToken'
import ToggleListItem from './ToggleListItem'
import TextFieldForm from './TextFieldForm'
import DeleteConfirm from './DeleteConfirm'
import BackListItem from './BackListItem'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Timestamp from './Timestamp'
import ListItem from '@mui/material/ListItem'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Code from './Code'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Tooltip from '@mui/material/Tooltip'
import { useOrgRole } from '../hooks/useOrgRole'
import { NETWORK_FORBIDDEN, toolRefusalMessage } from '../utilities/routerWrites'
import { KEY_DELETE_FORBIDDEN, KEY_FORBIDDEN, readKeySecret } from '../utilities/enrollmentKey'
import { callTool } from '../lib/tools'

const Token = () => {
  const navigate = useNavigate()
  const { tokenId } = useParams<{ tokenId: string }>()
  const { tokens, ...tokenModel } = useToken()
  const { networks } = useNetwork()
  const { copy, copied } = useClipboard({ copiedTimeout: 1500 })
  const [show, setShow] = useState<boolean>(false)
  // The refusal of the last change, as one sentence (app#373).
  const [error, setError] = useState<string | null>(null)
  // The key's secret, read through `network_key_read` on the first Show or Copy (app#379). It is
  // never on the `tokens` row: router migration 0216 withholds that column from the app.
  // ⚠ Held WITH the key's id, so an answer that arrives after the reader moved to another key is
  // never shown as that key's secret.
  const [held, setHeld] = useState<{ id: string; secret: string } | null>(null)
  // The router refused the read: this reader may not see the key, whatever the role says.
  const [refused, setRefused] = useState<boolean>(false)
  const [reading, setReading] = useState<boolean>(false)

  const token = tokens.find(t => t.id === tokenId)
  const network = networks.find(n => n.id === token?.networkId)
  // ⚠ A key that is not a key of a network of the active org sends the reader home (app#477).
  // The key list is not filtered to the active org, so the key itself can be in the store: what
  // says "not this org" is its network. The page drew such a key with no controls before.
  useCloseWhenMissing({
    id: tokenId,
    found: !!token && !!network,
    loadedOrgId: useNetwork(state => state.loadedOrgId),
    orgOf: useToken(state => state.orgOf),
    to: HOME,
  })
  // ⚠ The Owner/Admin half is asked in the NETWORK's org, as the router does. The key list is not
  // filtered to the active org, so a key can belong to a network of another org. Until the network
  // is known (null org), nothing is controlled.
  const { controls } = useOrgRole(network ? network.orgId : null)

  // A different key: forget the refusal and the error, and hide the key again.
  useEffect(() => {
    setRefused(false)
    setError(null)
    setShow(false)
  }, [tokenId])

  if (!token) return null

  // The network's owner or an org Owner/Admin manages its keys (app#373, docs/PERMISSIONS.md § 5
  // Network). The router's `network_key_*` tools refuse anyone else; this only hides the controls.
  const canManage = !!network && controls(network.ownerId)
  // ⚠⚠ The key panel shows only to who may READ the key (§ 5 Network: network owner or Admin).
  // Everyone else sees one sentence where the key would be. The router answers them `forbidden`.
  const canReadKey = canManage && !refused
  const secret = held && held.id === token.id ? held.secret : null

  // Returns the secret, reading it from the router the first time. Null when it could not.
  const loadSecret = async (): Promise<string | null> => {
    if (secret) return secret
    const id = token.id
    if (!id || reading) return null
    setReading(true)
    setError(null)
    const result = await readKeySecret(callTool, id)
    setReading(false)
    if (result.kind === 'secret') {
      setHeld({ id, secret: result.secret })
      return result.secret
    }
    if (result.kind === 'forbidden') setRefused(true)
    else setError(result.message)
    return null
  }

  const onShow = async () => {
    if (show) {
      setShow(false)
      return
    }
    if (await loadSecret()) setShow(true)
  }

  const onCopy = async () => {
    const value = await loadSecret()
    if (!value) return
    await copy(value)
    setShow(false)
  }

  // ⚠ Every change goes through the router and THROWS on a refusal, which is shown here. The page
  // leaves after a delete only when the key is gone.
  const run = async (act: () => Promise<void>, fallback: string): Promise<boolean> => {
    setError(null)
    try {
      await act()
      return true
    } catch (e) {
      console.error('enrollment key change failed:', e)
      setError(toolRefusalMessage(e, NETWORK_FORBIDDEN, fallback))
      return false
    }
  }

  const onDelete = async () => {
    if (!token.id) return
    if (await run(() => tokenModel.delete(token.id!), 'Could not delete the key.')) navigate(`/networks/${network?.id}`)
  }

  const onRename = (newName: string) => {
    if (token.id) void run(() => tokenModel.change(token.id!, { name: newName }), 'Could not rename the key.')
  }

  // ⚠ Only `enabled` is sent. The old upsert spread the whole row back, secret included.
  const onEnable = () => {
    if (token.id) void run(() => tokenModel.change(token.id!, { enabled: !token.enabled }), 'Could not change the key.')
  }

  return (
    <Stack sx={{
      gap: 3
    }}>
      <BackListItem to={`/networks/${network?.id}`}>{network?.name} network</BackListItem>
      <Box>
        <TextFieldForm
          label="Key Name"
          onSubmit={onRename}
          value={token?.name || ''}
          disabled={!canManage}
          sx={{ marginBottom: 1.5 }}
        />
        {canReadKey ? (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <Code className="ph-mask" sx={{ width: '100%', paddingX: 2 }}>
              {show && secret ? secret : '•••• •••• •••• ••••'}
            </Code>

            {/* Show/Hide Button */}
            <IconButton color="inherit" onClick={() => void onShow()} disabled={reading} sx={{ marginRight: 0.5 }}>
              {show ? <LuEyeOff /> : <LuEye />}
            </IconButton>

            {/* Copy Button */}
            <IconButton
              onClick={() => void onCopy()}
              disabled={reading}
              color={copied ? 'success' : 'inherit'}
              sx={{ marginRight: 0.5 }}
            >
              {copied ? <LuCheck /> : <LuCopy />}
            </IconButton>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {KEY_FORBIDDEN}
          </Typography>
        )}
      </Box>
      {/* ⚠⚠ A KEY ENROLS NODES. IT AUTHENTICATES NOTHING AFTER THAT, so neither
          control on this page can stop a node that already joined. Both strings said it
          did until app#258. An enrolled agent presents its own JWT, which the router
          checks against the network signing key plus `nodes.kid` and `nodes.revoked_at`
          (router `src/auth/signing.go`) — the `tokens` table is never read.
          `AuthenticateBearer` refuses a network key that carries a node id at all:
          "Reconnection via network key is no longer supported" (router
          `src/router/endpoint.go`).
          ⚠ Disable and delete differ only in the row: `GetTokenByKey` matches
          `enabled = true`, and the FK is `ON DELETE SET NULL`, so a delete nulls
          `nodes.token_id` and removes no node. Nothing cascades and no trigger fires.
          ⚠ What a delete DOES cost is re-entry: a machine that must enrol again — a
          reinstall, or a node whose JWT expired after `NOBGP_TOKEN_TTL` (30 days)
          offline — needs a key, and `nobgp register` is that recovery path.
          ⚠⚠ THE LAST SENTENCE MUST NOT PROMISE A DISCONNECT. It read "To disconnect a
          node, remove that node", which contradicts the Remove dialog in `Node.tsx` and
          `GetNodeByIdUnscoped`'s header: a remove is a soft delete and the agent's socket
          stays up. NOTHING in the app disconnects a live node — no Go path writes
          `nodes.revoked_at`, it is only ever read — so no string here may say one does. */}
      <Box>
        <Typography variant="h6">Security</Typography>
        <ToggleListItem checked={token.enabled} primary="Enabled" onClick={onEnable} disabled={!canManage} />
        <ListItem>
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            Disable to block new nodes. Nodes that already joined stay connected.
          </Typography>
        </ListItem>
        <Box sx={{
          marginTop: 2
        }}>
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            Created <Timestamp date={new Date(token.createdAt)} />
          </Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            Used <Timestamp date={new Date(token.usedAt)} />
          </Typography>
        </Box>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {/* ⚠ Same rule as the Key Name field and the Enabled switch above: the network's owner or an
          org Owner/Admin (app#422). Anyone else gets a greyed button and the reason, like Delete
          Network in Network.tsx and Remove Node in Node.tsx. A `disabled` on DeleteConfirm alone
          stopped the click but left the button red and live-looking. */}
      {canManage ? (
        <DeleteConfirm
          message="This blocks new nodes. Nodes that already joined stay connected, because they use their own credentials. A node that must join again later will need a key. Removing a node stops it from joining again, but it does not close a connection the node already has."
          onConfirm={onDelete}
        >
          <Button variant="outlined" color="error" startIcon={<LuTrash />} size="small">
            Delete Key
          </Button>
        </DeleteConfirm>
      ) : (
        <Tooltip title={KEY_DELETE_FORBIDDEN}>
          <span style={{ alignSelf: 'flex-start' }}>
            <Button variant="outlined" color="error" startIcon={<LuTrash />} size="small" disabled>
              Delete Key
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}

export default Token
